import { unByKey } from 'ol/Observable'
import Overlay from 'ol/Overlay'
import { getDistance } from 'ol/sphere'
import LayerGroup from 'ol/layer/Group'
import { toLonLat } from '../../../plugins/utils/projection'
import { NAUTICAL_MILE_IN_METERS } from '../../../plugins/utils/constantes'
import RouteLayer from '../../../plugins/layers/vector/RouteLayer'
import { FIRST_WAY_POINT, LAST_WAY_POINT, WAY_POINT_ROUTE_INDEX } from '../../../plugins/utils/features/RouteFeature'
import { round } from '../../../plugins/utils/helpers'

const ETA_WAY_POINT_STYLE = (feature) => {
  if (feature.get(FIRST_WAY_POINT)) return null
  else if (feature.get(LAST_WAY_POINT)) {
    return {
      font: '20px fontello',
      text: '\uf11e'
    }
  } else {
    return {
      font: 'bold 16px \'Open Sans\'',
      offsetY: '1',
      text: `${feature.get(WAY_POINT_ROUTE_INDEX)}`
    }
  }
}

export default class ETALayer extends LayerGroup {
  constructor(map) {
    super()
    this.map_ = map
    this.routes_ = {}
    this.currentShipId_ = null
  }

  paintRoute_(route) {
    const coords = route.feature.getGeometry().getCoordinates()
    const { tooltips } = route
    const tooltipToCreate = coords.length - tooltips.length - 1
    let tooltip
    for (let i = 0; i < tooltipToCreate; i++) {
      tooltip = new ToolTip()
      tooltips.push(tooltip)
      this.map_.addOverlay(tooltip)
    }
    for (let i = 0; i > tooltipToCreate; i--) {
      tooltip = tooltips.pop()
      this.map_.removeOverlay(tooltip)
    }
    const len = coords.length
    let totalDist = 0
    const latLongCoords = []
    let timeToDest
    for (const coord of coords) {
      latLongCoords.push(toLonLat(coord))
    }
    for (let i = 1; i < len; i++) {
      totalDist += getDistance(latLongCoords[i], latLongCoords[i - 1])
      if (Number(route.ship.sog) !== 0) {
        timeToDest = (totalDist * NAUTICAL_MILE_IN_METERS) / Number(route.ship.sog)
        route.arrival = new Date()
        route.arrival.setTime(route.arrival.getTime() + timeToDest)
      } else {
        route.arrival = null
      }
      tooltips[i - 1].innerHTML = createTooltipText(route.arrival, totalDist, route.ship.sog)
      tooltips[i - 1].static = i !== len - 1
      tooltips[i - 1].setPosition(coords[i])
    }
  }

  getRoute_(shipId) {
    return this.routes_[shipId]
  }

  addRoute(ship) {
    const routeLayer = new RouteLayer({ map: this.map_, wayPointStyle: ETA_WAY_POINT_STYLE })
    routeLayer.draw()
    for (const coord of ship.way) {
      routeLayer.addWayPoint(coord)
    }
    this.getLayers().push(routeLayer)
    const geomListener = routeLayer.getRoute().getGeometry().on('change', () => {
      this.paintRoute_(this.routes_[ship.id])
    })
    this.routes_[ship.id] = {
      layer: routeLayer, feature: routeLayer.getRoute(), listener: geomListener, ship, tooltips: []
    }
    routeLayer.stop()
  }

  setLightCondition(lightCondition) {
    for (const shipId of Object.keys(this.routes_)) {
      this.routes_[shipId].layer.setLightCondition(lightCondition)
    }
  }

  draw(shipInfo) {
    const routeLayer = new RouteLayer({ map: this.map_, wayPointStyle: ETA_WAY_POINT_STYLE })
    routeLayer.draw()
    routeLayer.addWayPoint(shipInfo.coord, false)
    this.getLayers().push(routeLayer)
    const geomListener = routeLayer.getRoute().getGeometry().on('change', () => {
      this.paintRoute_(this.routes_[shipInfo.id])
    })
    this.routes_[shipInfo.id] = {
      layer: routeLayer, feature: routeLayer.getRoute(), listener: geomListener, ship: shipInfo, tooltips: []
    }
    this.currentShipId_ = shipInfo.id
  }

  finish(shipId) {
    let shipId_ = shipId
    if (!shipId) shipId_ = this.currentShipId_
    const route = this.routes_[shipId_]
    if (route?.layer) route.layer.stop()
    this.currentShipId_ = null
    if (route) return { ...route.ship, arrival: route.arrival, way: route.feature.getGeometry().getCoordinates() }
    return null
  }

  clear(shipId) {
    let shipId_ = shipId
    if (!shipId) shipId_ = this.currentShipId_
    if (!shipId_) return
    this.finish(shipId_)
    const route = this.getRoute_(shipId_)
    if (route) {
      unByKey(route.listener)
      route.layer.clear()
      this.getLayers().remove(route.layer)
      for (const tooltip of route.tooltips) {
        this.map_.removeOverlay(tooltip)
      }
    }
    delete this.routes_[shipId_]
    return shipId_
  }

  clearAll() {
    for (const shipId of Object.keys(this.routes_)) {
      this.clear(shipId)
    }
  }

  updateShip(shipInfo) {
    const route = this.getRoute_(shipInfo.id)
    if (route) {
      route.ship = shipInfo
      const geom = route.feature.getGeometry()
      const coords = geom.getCoordinates()
      coords[0] = shipInfo.coord
      geom.setCoordinates(coords)
      if (route.editing) this.draw_.sketchCoords_[0] = coords[0]
      this.paintRoute_(route)
    }
  }

  getArrivalDate(shipId) {
    return this.routes_[shipId].arrival
  }
}

function createTooltipText(eta, distanceInMeter, sog) {
  let distance
  if (distanceInMeter > NAUTICAL_MILE_IN_METERS) {
    distance = `${round(distanceInMeter / NAUTICAL_MILE_IN_METERS, 2)}NM`
  } else {
    distance = `${Math.round(distanceInMeter)}m`
  }
  if (eta) {
    const year = eta.getFullYear()
    const month = eta.getMonth() + 1
    const day = eta.getDate()
    const min = eta.getMinutes()
    return `ETA: ${day < 10 ? `0${day}` : day}/${month < 10 ? `0${month}` : month}/${year} ${eta.getHours()}:${min < 10 ? `0${min}` : min} - ${distance} - ${sog}kn`
  } else {
    return `ETA: Unknown - ${distance}m - ${sog}kn`
  }
}

const ETA_TOOLTIP_CSS_CLASS = 'tooltip-eta'

class ToolTip extends Overlay {
  constructor() {
    const element = document.createElement('div')
    element.className = `tooltip ${ETA_TOOLTIP_CSS_CLASS}`

    super({
      stopEvent: false,
      element,
      offset: [0, -7],
      positioning: 'bootom-center'
    })
  }

  get innerHTML() {
    return this.getElement().innerHTML
  }

  set innerHTML(html) {
    this.getElement().innerHTML = html
  }
}